import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import {
  getDevelopersOptions,
  getJobsiteOptions,
} from 'containers/jobsites/helpers/utils';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { Jobsite, UserData } from './types';
import {
  getJobsiteInfoSectionInputs,
  getJobsiteInfoSectionDefaultValues,
  getJobsiteInfoSectionUpdateInput,
} from './jobsiteInfoSection';

export type EditJobsiteConfigTabFormData = Pick<
  EditJobsiteConfigurationFormData,
  'jobsite'
>;

export type JobsiteSelectOptions = {
  developersOptions: SelectOptionElement[];
  jobsitesOptions: SelectOptionElement[];
};

export const getDropdownsOptions = (args: {
  userData: UserData;
  jobsite: Jobsite;
}): JobsiteSelectOptions => {
  const { userData, jobsite } = args;
  const developers = userData?.developers.edges.map(({ node }) => node);
  const jobsites = userData?.jobsites.edges
    .filter(({ node }) => node?.jobsiteId !== jobsite?.jobsiteId) // filter out current jobsite
    .map(({ node }) => node);

  return {
    developersOptions: getDevelopersOptions(developers),
    jobsitesOptions: getJobsiteOptions(jobsites),
  };
};

type GetFormInputsArgs = JobsiteSelectOptions & {
  jobsite: Jobsite;
};

export const getFormInputs = (
  args: GetFormInputsArgs,
): TypedFormInputs<EditJobsiteConfigTabFormData> => {
  const { jobsite, developersOptions, jobsitesOptions } = args;

  const jobsiteInfoSectionInputs = getJobsiteInfoSectionInputs({
    developersOptions,
    jobsitesOptions,
    isTemplate: jobsite?.isTemplate,
  });

  return {
    jobsite: {
      element: FormInputTypes.Section,
      layout: '',
      elementProps: {
        title: 'Jobsite Info',
        childrenGridColumns: '4',
      },
      children: jobsiteInfoSectionInputs,
    },
  };
};

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
} & JobsiteSelectOptions;

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const { jobsite, developersOptions, jobsitesOptions } = args;

  return {
    jobsite: getJobsiteInfoSectionDefaultValues(
      jobsite,
      developersOptions,
      jobsitesOptions,
    ),
  };
};

export const getJobsiteUpdateInput = async (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): Promise<JobsiteUpdateInput> => {
  const jobsiteInfoSectionUpdateInput = await getJobsiteInfoSectionUpdateInput(
    data.jobsite,
    dirtyFields.jobsite,
  );
  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    ...jobsiteInfoSectionUpdateInput,
  });
};
